import { Dispatch } from 'redux'
import {
  BUSINESS_PORTAL_LINK,
  UPSHIFTER_PORTAL_LINK,
} from 'config/envVariables'
import { ResetAuthReducer as ResetAuthReducerAction } from 'data/common/Auth/actions'
import { AuthActions } from 'data/common/Auth/thunks'
import {
  DOMAIN,
  ITrackingUserValues,
  LOGIN_METHOD,
  TrackingService,
} from 'helpers/TrackingService'
import { Events, IEvents } from 'helpers/TrackingService/events'
import { isMobile } from 'helpers/helperFunctions'
import {
  isUserBusiness,
  isUserUpshifter,
} from 'helpers/permissionsHelpers/roleHelpers'
import { USER_ROLE } from 'models/Auth'
import { IEnumObject, TypeOrNull } from 'models/Common'
import { POPUP_ACTION_TYPE } from 'models/Shift'
import { IUser, UserRole } from 'models/User'
import ROUTES from 'routes/routes'

export interface IGeolocationPositionOptions {
  timeout?: number
  maximumAge?: number
  enableHighAccuracy?: boolean
  distanceFilter?: number
  useSignificantChanges?: boolean
}

export interface ILocation {
  latitude: TypeOrNull<number>
  longitude: TypeOrNull<number>
  status: boolean
  userPickedLocation: boolean
  timestamp: number
  city?: string
  regionId?: number
}

export type Procedure = (...args: any[]) => void

export type Options = {
  isImmediate: boolean
}

type TProperties = {
  [key: string]: any
}

/**
 * A function that emits a side effect and does not return anything.
 */
export function debounce<F extends Procedure>(
  func: F,
  waitMilliseconds = 50,
  options: Options = {
    isImmediate: false,
  },
): (this: ThisParameterType<F>, ...args: Parameters<F>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined

  return function(this: ThisParameterType<F>, ...args: Parameters<F>) {
    const context = this

    const doLater = function() {
      timeoutId = undefined
      if (!options.isImmediate) {
        func.apply(context, args)
      }
    }

    const shouldCallNow = options.isImmediate && timeoutId === undefined

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId)
    }

    timeoutId = setTimeout(doLater, waitMilliseconds)

    if (shouldCallNow) {
      func.apply(context, args)
    }
  }
}

// Redux helper functions
export const updateArray = <T extends { id: number; [key: string]: any }>(
  data: T[],
  payload: T,
): T[] => {
  const updatedArray = data.map(item => {
    if (item.id !== payload.id) {
      return item
    }
    return payload
  })
  return updatedArray
}

export const deleteFromArray = <T extends { id: number; [key: string]: any }>(
  data: T[],
  id: number,
): T[] => {
  const updatedArray = data.filter(item => item.id !== id)
  return updatedArray
}

export const addToArray = <T extends { id: number; [key: string]: any }>(
  item: T,
  data: T[],
): T[] => [item, ...data]

export const maybeAddToArray = <T extends { id: number; [key: string]: any }>(
  data: T[],
  item: T,
): T[] => {
  const exists = data.find(element => element.id === item.id)

  if (!exists) {
    return [item, ...data]
  } else return data
}

/**
 * Creates a file from a Blob received from an API and downloads it
 * @param response The response that should be put in a file
 * @param fileName How should the file be named when downloaded
 */
export const createAndDownloadFile = (response: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([response]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
}

/**
 * Create valid form data from a simple or a complex/nested object
 * @param object
 * @param form
 * @param namespace
 */
export const createFormDataFromObject = (
  object: Object,
  form?: FormData,
  namespace?: string,
): FormData => {
  const formData = form || new FormData()
  for (const property in object) {
    if (!object.hasOwnProperty(property) || !(object as any)[property]) {
      continue
    }
    const formKey = namespace ? `${namespace}[${property}]` : property
    if ((object as any)[property] instanceof Date) {
      formData.append(formKey, (object as any)[property].toISOString())
    } else if (
      typeof (object as any)[property] === 'object' &&
      !((object as any)[property] instanceof File)
    ) {
      createFormDataFromObject((object as any)[property], formData, formKey)
    } else {
      formData.append(formKey, (object as any)[property])
    }
  }
  return formData
}

/**
 * This function transforms the user object we fetch from BND
 * by adding a role attribute
 * @param user User object which we fetch from the BND
 * @returns User object with role attribute added
 */
export const addRoleToUser = (user: IUser) => {
  let updatedUser = user
  switch (user.roles[0].name) {
    case UserRole.BUSINESS:
      updatedUser = { ...updatedUser, role: updatedUser.roles[0].name }
      break
    case UserRole.APPROVER:
    case UserRole.VIEWER:
    case UserRole.FINANCE_MANAGER:
    case UserRole.EMPLOYEE:
      updatedUser = {
        ...updatedUser,
        role: updatedUser.roles[0].name,
        business_id: user.employer_business_id,
      }
      break
    case 'labor':
      updatedUser = { ...updatedUser, role: USER_ROLE.UPSHIFTER }
      break
    default:
      break
  }
  return updatedUser
}

/**
 * Generates and Downloads a file
 * @param response (Blob)
 * @param fileName (File name string excluding the extension)
 * @param ext (Downloaded file extension)
 */
export const createAndDownloadReport = (
  response: Blob,
  fileName: string,
  ext: 'xls' | 'csv' | 'zip',
) => {
  const url = window.URL.createObjectURL(new Blob([response]))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', `${fileName}.${ext}`)
  document.body.appendChild(link)
  link.click()
}

/**
 * Promisified geolocation api
 *
 * @param options Geolocation options
 * @returns
 */
export const getPosition = (
  options?: IGeolocationPositionOptions,
): Promise<GeolocationPosition> => {
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, options),
  )
}

/**
 * Set location object in local storage
 *
 * @param location Location object
 */
export const setLocation = (location: ILocation) => {
  localStorage.setItem('location', JSON.stringify(location))
}

/**
 * Fetches a file from URL. It is converted to blob and the blob is used to construct a File object (ready to use in Dropzone).
 * We need the file in this format so we can preview the attachment within Dropzone component from the API request.
 * @param {*} attachmentUrl
 * @param {*} fileName
 * @returns File || null
 */
export const convertFromUrlToObject = async (
  attachmentUrl: string,
  fileName: string,
) => {
  try {
    const attachment = await fetch(attachmentUrl)
    const blob = await attachment.blob()
    const file = new File([blob], fileName, { type: blob.type })
    return file
  } catch (error) {
    return null
  }
}

/**
 * This function returns the users location if location permission is allowed
 * and returns an object with latitude: null and longitude: null
 * if the users' location permission is declined
 */
export const fetchLocation = async (): Promise<ILocation> => {
  const { geolocation } = navigator
  return new Promise((resolve, reject) => {
    const currentDate = new Date()
    geolocation.getCurrentPosition(
      // This executes when the user clicks Allow location
      position => {
        const { coords } = position
        const { latitude, longitude } = coords
        const locationLocked = {
          latitude,
          longitude,
          status: true,
          userEnabled: true,
          userPickedLocation: false,
          timestamp: currentDate.getTime(),
        }
        localStorage.setItem('location', JSON.stringify(locationLocked))
        resolve(locationLocked)
      },
      // This executes when the user clicks Decline location
      () => {
        const locationLocked = {
          latitude: null,
          longitude: null,
          userEnabled: false,
          status: false,
          userPickedLocation: false,
          timestamp: currentDate.getTime(),
        }
        resolve(locationLocked)
      },
    )
  })
}

/**
 * This function checks if the logged in user is on the correct domain,
 * if he's not on the correct domain, he's being redirected and logged in automatically
 * using the access token we get from the BND which we pass to the authentication route
 * of the specified project
 * @param role - the role of the user that is logging into the application
 * @param token - the access token of the user that is logging into the application
 * @returns boolean - returns a boolean value which gives the information if the user is being redirected
 */
export const redirectUserToSplitDomains = (
  role: USER_ROLE,
  token: string,
  dispatch: Dispatch<AuthActions>,
) => {
  if (isUserBusiness(role) && window.location.origin !== BUSINESS_PORTAL_LINK) {
    dispatch(ResetAuthReducerAction())
    window.location.assign(
      `${BUSINESS_PORTAL_LINK}${ROUTES.AUTHENTICATION}?token=${token}`,
    )
    return true
  } else if (
    isUserUpshifter(role) &&
    window.location.origin !== UPSHIFTER_PORTAL_LINK
  ) {
    if (window.location.origin.startsWith('https://app.local.upshift.work')) {
      return false
    } else {
      dispatch(ResetAuthReducerAction())
      window.location.assign(
        `${UPSHIFTER_PORTAL_LINK}${ROUTES.AUTHENTICATION}?token=${token}`,
      )
      return true
    }
  }
  return false
}

// Function that checks if background check is needed when apply/claim for a shift
export const checkIfShouldAskForBackgroundCheck = (
  pop_up_action_type?: POPUP_ACTION_TYPE,
) =>
  pop_up_action_type !== undefined &&
  (pop_up_action_type === POPUP_ACTION_TYPE.POP_UP_ACTION_OKAY ||
    pop_up_action_type === POPUP_ACTION_TYPE.POP_UP_ACTION_SUBMIT_CONSENT ||
    pop_up_action_type ===
      POPUP_ACTION_TYPE.POP_UP_ACTION_WOULD_LIKE_TO_SUBMIT_CONSENT)

// Function that checks if drug testing is needed when apply/claim for a shift
export const checkIfShouldAskForDrugTesting = (
  pop_up_action_type?: POPUP_ACTION_TYPE,
) =>
  pop_up_action_type !== undefined &&
  pop_up_action_type === POPUP_ACTION_TYPE.POP_UP_ACTION_DRUG_SCREENING

// Function that checks if hepatitis file upload is needed when apply/claim for a shift
export const checkIfShouldAskForHepatitis = (
  pop_up_action_type?: POPUP_ACTION_TYPE,
) =>
  pop_up_action_type !== undefined &&
  pop_up_action_type === POPUP_ACTION_TYPE.POP_UP_ACTION_HEPATITIS_A_SCREENING

// Function that returns the screen resolution as a string (ex. "1920x1080")
export const getScreenResolution = () => {
  const screenWidth = window.screen.width
  const screenHeight = window.screen.height
  const screenResolution = `${screenWidth}x${screenHeight}`

  return screenResolution
}

// Function that returns the browser name (covers only the major browser names)
export const getBrowser = () => {
  const userAgent = navigator.userAgent

  if (userAgent.includes('Opera') || userAgent.includes('OPR')) {
    return 'Opera'
  }
  if (userAgent.includes('Edg')) {
    return 'Edge'
  }
  if (userAgent.includes('Chrome')) {
    return 'Chrome'
  }
  if (userAgent.includes('Safari')) {
    return 'Safari'
  }
  if (userAgent.includes('Firefox')) {
    return 'Firefox'
  }
  if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    return 'IE'
  }
  return 'unknown'
}

// Function that returns the OS name
export const getOS = () => {
  const userAgent = navigator.userAgent

  if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    return 'iOS'
  }
  if (userAgent.includes('Mac')) {
    return 'Mac OS'
  }
  if (userAgent.includes('Windows')) {
    return 'Windows'
  }
  // The check for Android should always come before the Linux check, because in some cases the Android user agent string contains Linux
  if (userAgent.includes('Android')) {
    return 'Android'
  }
  if (userAgent.includes('Linux')) {
    return 'Linux'
  }
  return 'unknown'
}

// Function that tracks some parameters when the user successfully logs in
export const trackLoginSuccessEvent = (
  loginMethod: LOGIN_METHOD,
  userValues: ITrackingUserValues,
) => {
  TrackingService.track({
    event: Events.LoginSuccess,
    properties: {
      domain: isMobile() ? DOMAIN.MOBILE : DOMAIN.WEB,
      userId: userValues.id,
      userEmail: userValues.email,
      region: userValues.region,
      browser: getBrowser(),
      os: getOS(),
      screenResolution: getScreenResolution(),
      loginMethod,
    },
  })
}

// Function that takes a string from the Events object and tracks the given event
export const trackLoginEvent = (event: IEvents[keyof IEvents]) => {
  TrackingService.track({
    event,
    properties: {
      domain: isMobile() ? DOMAIN.MOBILE : DOMAIN.WEB,
      browser: getBrowser(),
      os: getOS(),
      screenResolution: getScreenResolution(),
    },
  })
}

// Function that takes a string from the Events object and tracks the given event
export const trackFilterEvents = (
  event: IEvents[keyof IEvents],
  properties: TProperties,
) => {
  TrackingService.track({
    event,
    properties: {
      ...properties,
      domain: isMobile() ? DOMAIN.MOBILE : DOMAIN.WEB,
      browser: getBrowser(),
      os: getOS(),
      screenResolution: getScreenResolution(),
    },
  })
}

// Function that transforms objects that have pairs of an ID and name to an array used for Dropdowns
export const transformObjectToArray = (obj: IEnumObject) => {
  return Object.entries(obj).map(([key, label]) => ({
    value: parseInt(key),
    label,
  }))
}

/**
 * Masks sensitive information in a string by replacing all characters
 * with asterisks except the specified number of visible digits from the end.
 *
 * @param {string} inputString - The input string to be masked
 * @param {number} visibleDigits - The number of digits to keep visible from the end
 * @returns {TypeOrNull<string>} The masked string or null if the input string is empty
 */
export const maskString = (
  inputString: string,
  visibleDigits: number,
): TypeOrNull<string> => {
  if (!inputString) return null
  const hiddenDigits = inputString.length - visibleDigits
  const maskedString =
    '*'.repeat(hiddenDigits) + inputString.slice(hiddenDigits)

  return maskedString
}
